import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { supabase } from '../services/supabase';
import { FEATURE_ACCESS, type Plan } from '../config/subscriptionFeatures';

export interface UserSubscription {
  user_id: string;
  plan: Plan;
  status: 'active' | 'inactive';
  save_credits: number;
  save_limit: number;
  saved_schedules_count: number;
  created_at?: string;
  updated_at?: string;
}

const VALID_PLANS: Plan[] = ['basic', 'standard', 'premium'];
const VALID_STATUSES = ['active', 'inactive'];

/** Type-check a Realtime payload before trusting it as a UserSubscription. */
const isValidSubscription = (obj: unknown): obj is UserSubscription => {
  if (!obj || typeof obj !== 'object') return false;
  const o = obj as Record<string, unknown>;
  return (
    typeof o.user_id === 'string' &&
    typeof o.plan === 'string' && VALID_PLANS.includes(o.plan as Plan) &&
    typeof o.status === 'string' && VALID_STATUSES.includes(o.status as string) &&
    typeof o.save_credits === 'number' &&
    typeof o.save_limit === 'number' &&
    typeof o.saved_schedules_count === 'number'
  );
};

interface SubscriptionContextType {
  subscription: UserSubscription | null;
  loading: boolean;
  canAccessFeature: (feature: string) => boolean;
  remainingSaveCredits: number;
  addSaveCredits: (credits: number) => Promise<void>;
  useSaveCredit: () => Promise<boolean>;
  /** Spend one general-purpose credit (used for unlock/restart after finalization). */
  spendCredit: () => Promise<boolean>;
  setSubscription: (subscription: UserSubscription) => void;
  canSaveMoreSchedules: () => boolean;
  getSaveLimit: () => number;
  refreshSavedSchedulesCount: () => Promise<void>;
  refreshSubscription: () => Promise<void>;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

// Subscription hooks live next to the provider to preserve the existing public import path.
// eslint-disable-next-line react-refresh/only-export-components
export function useSubscription() {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
}

export function SubscriptionProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState<UserSubscription | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setSubscription(null);
      setLoading(false);
      return;
    }

    async function loadSubscription() {
      try {
        const { data, error } = await supabase
          .from('user_subscriptions')
          .select('*')
          .eq('user_id', user!.id)
          .single();

        if (error) {
          // No row yet - create a default basic one (handles race with signup trigger)
          if (error.code === 'PGRST116') {
            const { data: newSub, error: createError } = await supabase
              .from('user_subscriptions')
              .insert({
                user_id: user!.id,
                plan: 'basic',
                status: 'active',
                save_credits: 3,
                save_limit: 3,
                saved_schedules_count: 0,
              })
              .select()
              .single();

            if (createError) {
              console.error('Error creating subscription:', createError);
              setSubscription(null);
            } else {
              setSubscription(newSub);
            }
          } else {
            console.error('Error fetching subscription:', error);
            setSubscription(null);
          }
        } else {
          setSubscription(data);
        }
      } catch (err) {
        console.error('Error loading subscription:', err);
        setSubscription(null);
      } finally {
        setLoading(false);
      }
    }

    loadSubscription();

    // Listen for real-time changes to this user's subscription row so the
    // plan badge updates the moment the server writes the purchase result,
    // without requiring a manual page refresh.
    const channel = supabase
      .channel(`subscription:${user.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_subscriptions',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          if (payload.new && Object.keys(payload.new).length > 0) {
            // Validate the payload shape before trusting it
            if (isValidSubscription(payload.new)) {
              setSubscription(payload.new);
            }
          }
        }
      )
      .subscribe();

    return () => {
      channel.unsubscribe().then(() => {
        supabase.removeChannel(channel);
      });
    };
  }, [user]);

  const canAccessFeature = (feature: string): boolean => {
    if (!subscription) return false;
    return FEATURE_ACCESS[subscription.plan].includes(feature);
  };

  const getSaveLimit = (): number => {
    if (!subscription) return 0;
    return subscription.save_limit;
  };

  const canSaveMoreSchedules = (): boolean => {
    if (!subscription) return false;

    // If they have additional save credits, they can always save more
    if (subscription.save_credits > 0) return true;

    // Otherwise, check if they have reached their plan's save limit
    return subscription.saved_schedules_count < subscription.save_limit;
  };

  const refreshSavedSchedulesCount = async (): Promise<void> => {
    if (!user || !subscription) return;

    try {
      const { data, error } = await supabase
        .from('user_subscriptions')
        .select('saved_schedules_count')
        .eq('user_id', user.id)
        .single();

      if (error) throw error;

      setSubscription({
        ...subscription,
        saved_schedules_count: data.saved_schedules_count
      });
    } catch (err) {
      console.error('Error refreshing saved schedules count:', err);
    }
  };

  const remainingSaveCredits = (): number => {
    if (!subscription) return 0;
    return subscription.save_credits;
  };

  const addSaveCredits = async (credits: number): Promise<void> => {
    if (!user || !subscription) return;

    try {
      // Use atomic RPC to avoid race conditions
      const { error } = await supabase.rpc('add_save_credits', {
        p_user_id: user.id,
        p_credits: credits
      });

      if (error) throw error;

      // Refresh from DB to get the authoritative value
      await refreshSubscription();
    } catch (err) {
      console.error('Error adding save credits:', err);
      throw err;
    }
  };

  const refreshSubscription = async (): Promise<void> => {
    if (!user) return;
    try {
      const { data, error } = await supabase
        .from('user_subscriptions')
        .select('*')
        .eq('user_id', user.id)
        .single();
      if (error) throw error;
      setSubscription(data);
    } catch (err) {
      console.error('Error refreshing subscription:', err);
    }
  };

  const spendCredit = async (): Promise<boolean> => {
    if (!user || !subscription || subscription.save_credits <= 0) return false;
    try {
      // Atomic decrement via RPC - the DB function checks save_credits > 0
      // and returns false if there aren't enough credits
      const { data, error } = await supabase.rpc('spend_save_credit', {
        p_user_id: user.id
      });

      if (error) throw error;
      if (!data) return false; // no credits available (race condition caught by DB)

      // Refresh from DB to get the authoritative state
      await refreshSubscription();
      return true;
    } catch (err) {
      console.error('Error spending credit:', err);
      throw err;
    }
  };

  const useSaveCredit = async (): Promise<boolean> => {
    if (!user || !subscription) return false;

    try {
      // Use atomic RPC that handles the plan-limit vs credit logic in a single
      // database call, preventing race conditions from concurrent tabs.
      const { data, error } = await supabase.rpc('use_save_credit', {
        p_user_id: user.id
      });

      if (error) throw error;
      if (!data) return false;

      // Refresh from DB to get the authoritative state
      await refreshSubscription();
      return true;
    } catch (err) {
      console.error('Error using save credit:', err);
      throw err;
    }
  };

  return (
    <SubscriptionContext.Provider
      value={{
        subscription,
        loading,
        canAccessFeature,
        remainingSaveCredits: remainingSaveCredits(),
        addSaveCredits,
        useSaveCredit,
        spendCredit,
        setSubscription,
        canSaveMoreSchedules,
        getSaveLimit,
        refreshSavedSchedulesCount,
        refreshSubscription,
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
}
